/**
 * Authentication Service - TypeScript
 * Portal ICAVI - Sprint 4 Migration
 * Serviço de autenticação com JWT, refresh tokens e interceptors
 */
import axios from 'axios';
import { WEB_CSRF_COOKIE_NAMES } from './authSession';
const readCookie = (name) => {
    if (typeof document === 'undefined') {
        return null;
    }
    const prefix = `${name}=`;
    const rawCookie = document.cookie
        .split(';')
        .map(cookie => cookie.trim())
        .find(cookie => cookie.startsWith(prefix));
    if (!rawCookie) {
        return null;
    }
    try {
        return decodeURIComponent(rawCookie.slice(prefix.length));
    }
    catch {
        return rawCookie.slice(prefix.length);
    }
};
const addWebCsrfHeader = (config) => {
    if (['get', 'head', 'options'].includes((config.method || 'get').toLowerCase())) {
        return;
    }
    const csrfToken = WEB_CSRF_COOKIE_NAMES.map(name => readCookie(name)).find(Boolean);
    if (csrfToken) {
        config.headers['X-ICAVI-CSRF'] = csrfToken;
    }
};
// ============================================================================
// API Configuration
// ============================================================================
/**
 * Detecta automaticamente a URL base da API baseada no hostname atual
 */
const getApiBaseUrl = () => {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;
    // Se acessando via nginx (porta 80, 443, ou sem porta), usar URL relativa para proxy
    if (!port || port === '' || port === '80' || port === '443' || port === '3001') {
        return '';
    }
    // Se estÃ¡ sendo acessado via localhost:8080 (webpack-dev-server), usar localhost:5000
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && port === '8080') {
        return 'http://localhost:5000';
    }
    // Para qualquer outro IP (incluindo VLANs), FORÇAR HTTP explicitamente para rede local
    // Isso previne que o navegador force HTTPS devido a cache/HSTS
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
        // Verificar se é IP local (192.168.x.x, 10.x.x.x, 172.16-31.x.x)
        const isLocalIP = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.)/.test(hostname);
        const forcedProtocol = isLocalIP ? 'http' : protocol;
        const apiUrl = `${forcedProtocol}//${hostname}:5000`;
        return apiUrl;
    }
    // Fallback para URL relativa (para funcionar com proxy)
    return '';
};
const API_BASE_URL = getApiBaseUrl();
/**
 * InstÃ¢ncia do axios configurada
 */
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    // O POST /api/auth/web espera o desafio MFA no mesmo request.
    // Em produção isso passou de 10s e o axios abortava com o toast genérico.
    // 45s cobre esse caso e o SMTP (30s) sem chegar no proxy_read_timeout do nginx (60s).
    timeout: 45000,
});
// ============================================================================
// Axios Interceptors
// ============================================================================
/**
 * Interceptor de requisição: adiciona token de autenticação
 */
api.interceptors.request.use((config) => {
    delete config.headers.Authorization;
    addWebCsrfHeader(config);
    return config;
}, (error) => {
    return Promise.reject(error);
});
/**
 * A renovação da sessão Web é coordenada pelo cliente HTTP centralizado.
 * Esta instância apenas preserva o erro para o chamador de autenticação.
 */
api.interceptors.response.use(response => response, (error) => Promise.reject(error));
// ============================================================================
// Authentication Service
// ============================================================================
/**
 * Serviço de autenticação com métodos para login, registro, alterações de senha e refresh token
 */
export const authService = {
    /**
     * Realiza login do usuário
     * @param credentials Credenciais de login (username e password)
     * @returns Resposta do login com dados do usuário e tokens
     */
    async login(credentials) {
        try {
            const payload = {
                username: credentials.username,
                senha: credentials.senha ?? credentials.password ?? '',
            };
            if (!payload.username || !payload.senha) {
                return {
                    status: 'erro',
                    mensagem: 'Usuário e senha são obrigatórios.',
                };
            }
            await api.get('/api/auth/web/bootstrap');
            const response = await api.post('/api/auth/web', payload);
            return response.data;
        }
        catch (error) {
            console.error('Erro no login:', error);
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    /**
     * Registra um novo usuário
     * @param userData Dados do usuário para registro
     * @returns Resposta do registro
     */
    async register(userData) {
        try {
            const response = await api.post('/api/usuarios', userData);
            return response.data;
        }
        catch (error) {
            console.error('Erro no registro:', error);
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    /**
     * Altera a senha do usuário
     * @param passwordData Dados de alteração de senha (senhaAtual e novaSenha)
     * @returns Resposta da alteração de senha
     */
    async changePassword(passwordData) {
        try {
            const response = await api.post('/api/alterar-senha', passwordData);
            return response.data;
        }
        catch (error) {
            console.error('Erro ao alterar senha:', error);
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    async requestPasswordReset(data) {
        try {
            const response = await api.post('/api/esqueci-senha', data);
            return response.data;
        }
        catch (error) {
            console.error('Erro ao solicitar reset de senha:', error);
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    async confirmPasswordReset(data) {
        try {
            const response = await api.post('/api/redefinir-senha', data);
            return response.data;
        }
        catch (error) {
            console.error('Erro ao redefinir senha:', error);
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    /**
     * Renova a sessão Web usando os cookies HttpOnly; o argumento é mantido
     * apenas para compatibilidade de chamadas antigas e nunca é enviado.
     */
    async refreshToken(_refreshToken = '') {
        try {
            // O middleware Web valida Content-Type em todo POST; o endpoint não
            // precisa de campos, mas ainda deve receber um corpo JSON vazio.
            const response = await api.post('/api/auth/web/refresh', {});
            return response.data;
        }
        catch (error) {
            console.error('Erro ao renovar token:', error);
            if (axios.isAxiosError(error) && error.response?.data) {
                return {
                    ...error.response.data,
                    http_status: error.response.status,
                };
            }
            throw error;
        }
    },
    async getCurrentUser() {
        try {
            const response = await api.get('/api/auth/me');
            return response.data;
        }
        catch (error) {
            if (axios.isAxiosError(error) && error.response?.data) {
                return {
                    ...error.response.data,
                    http_status: error.response.status,
                };
            }
            throw error;
        }
    },
    async completeMfaChallenge(code, useRecoveryCode = false) {
        try {
            const response = await api.post('/api/auth/mfa/challenge', {
                code,
                ...(useRecoveryCode ? { recovery: true } : {}),
            });
            return response.data;
        }
        catch (error) {
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    async startMfaEnrollment(method, secret, email) {
        try {
            const response = await api.post('/api/auth/mfa/enroll', {
                method,
                ...(secret ? { secret } : {}),
                ...(email ? { email } : {}),
            });
            return response.data;
        }
        catch (error) {
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    async completeMfaEnrollment(code) {
        try {
            const response = await api.post('/api/auth/mfa/enroll', { code });
            return response.data;
        }
        catch (error) {
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    async completePasswordChangeContext(currentPassword, newPassword) {
        try {
            const response = await api.post('/api/auth/password-change', {
                senha_atual: currentPassword,
                nova_senha: newPassword,
            });
            return response.data;
        }
        catch (error) {
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    async logout() {
        try {
            // O middleware Web exige Content-Type JSON em todo POST, mesmo sem campos.
            const response = await api.post('/api/auth/web/logout', {});
            return response.data;
        }
        catch (error) {
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
};
/**
 * Exporta a instância do axios para uso geral
 */
export default api;
